import type { CalendarFeed, CalendarItem } from "@ronin/contracts";
import { zonedParts } from "@ronin/core";
import { calendarColors, ownEventColor } from "@ronin/ui";
import { ordinal } from "../../lib/calendar-view.js";

/** Stripe and tint of an item: own events in the accent, feed events in their feed's colour. */
export function itemColor(item: CalendarItem, feeds: ReadonlyMap<string, CalendarFeed>) {
  if (item.source === "feed") {
    const feed = feeds.get(item.feedId);
    return feed ? calendarColors[feed.color] : calendarColors["cal-6"];
  }
  return ownEventColor;
}

export const isBirthday = (item: CalendarItem) =>
  item.source === "event" && item.event.kind === "birthday";

/** "Anna · 30th birthday" for birthdays with a birth year, else the title. */
export function itemTitle(item: CalendarItem): string {
  if (item.source === "event" && item.event.kind === "birthday" && item.age !== null) {
    return `${item.title} · ${ordinal(item.age)} birthday`;
  }
  return item.title;
}

const shortDate = (date: string) =>
  new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", timeZone: "UTC" }).format(
    new Date(`${date}T00:00:00Z`),
  );

/** When an item happens, for labels and details: "All day · 24 Oct – 26 Oct", "18:00 – 19:00". */
export function describeTiming(item: CalendarItem, timeZone: string): string {
  if (item.allDay && item.startDate && item.endDate) {
    return item.startDate === item.endDate
      ? `All day · ${shortDate(item.startDate)}`
      : `All day · ${shortDate(item.startDate)} – ${shortDate(item.endDate)}`;
  }
  if (!item.startsAt || !item.endsAt) return "";
  const start = zonedParts(new Date(item.startsAt), timeZone);
  const end = zonedParts(new Date(item.endsAt), timeZone);
  if (start.date === end.date) return `${shortDate(start.date)} · ${start.time} – ${end.time}`;
  return `${shortDate(start.date)} ${start.time} – ${shortDate(end.date)} ${end.time}`;
}

/** Accessible name: title, timing and, for feed events, the feed. */
export function describeItem(
  item: CalendarItem,
  timeZone: string,
  feeds: ReadonlyMap<string, CalendarFeed>,
): string {
  const parts = [itemTitle(item), describeTiming(item, timeZone)];
  if (item.source === "feed") parts.push(feeds.get(item.feedId)?.name ?? "Feed");
  return parts.filter(Boolean).join(", ");
}
